import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, BoxProps, HStack, IconButton, Spinner, Text } from '@chakra-ui/react';
import { HoverCardContent, HoverCardRoot, HoverCardTrigger } from '@src/components/chakra-ui/hover-card';
import { Tooltip } from '@src/components/chakra-ui/tooltip';
import { ChatGenerateControl } from '@src/components/sco/Chat/ChatGenerateControl';
import { ChatTypingIndicator } from '@src/components/sco/Chat/ChatThreadTypingIndicator';
import { ChatConnectionStatus } from '@src/components/sco/Chat/Layout/ChatConnectionStatus';
import { CommentPendingAttachments, COMMENT_STORED_FILES_SELECTION, useCommentAttachments } from '@src/components/sco/Comments/CommentAttachments';
import { OnboardingHighlightTarget, ONBOARDING_HIGHLIGHT_IDS } from '@src/components/sco/Requests/RequestOnboarding/OnboardingHighlightTarget';
import { RichMarkdownInput } from '@src/components/sco/RichMarkdownInput';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useDebounce } from '@uidotdev/usehooks';
import React, { useEffect, useRef, useState } from 'react';
import { LuArrowUp, LuPlus, LuSave, LuSend, LuTrash2 } from 'react-icons/lu';
type ChatDraft = {
  commentId?: string | null;
  body?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};
type Props = {
  boxProps?: BoxProps;
  newCommentValues: {
    attachmentId?: string;
    attachmentType?: string;
    parentCommentId?: string;
    parentCommentNodeId?: string;
    threadId?: string;
  };
  onSuccess?: (mutationResponse) => Promise<any> | any;
  onError?: (error) => Promise<any> | any;
  onCancel?: () => Promise<any> | any;
  placeholder?: string;
  enableFileAttachments?: boolean;
};
export const ChatMessageComposer = (props: Props) => {
  const {
    currentUser
  } = useCurrentUser();
  const [body, setBody] = useState('');
  const [draftCommentId, setDraftCommentId] = useState<string | null>(null);
  const [editorSeed, setEditorSeed] = useState('new');
  const [initialMarkdown, setInitialMarkdown] = useState('');
  const debouncedBody = useDebounce(body, 3000);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitTrigger, setSubmitTrigger] = useState(0);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [generateBusy, setGenerateBusy] = useState(false);
  const {
    pendingFiles,
    addFile,
    removeFile,
    reset,
    isUploading,
    isFull,
    storedFileIds
  } = useCommentAttachments();
  const [upsertChatComment, upsertChatCommentMutation] = useMutation(ChatMessageComposer.mutations.upsertChatComment());
  const [deleteComment] = useMutation(ChatMessageComposer.mutations.deleteComment());
  const threadId = props.newCommentValues.threadId;
  const parentCommentId = props.newCommentValues.parentCommentId;
  const threadIdRef = useRef(threadId);
  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);
  const canUseDrafts = !!currentUser?.isAdmin;
  // Generate is sco-admin only: the mutation refuses every other admin.
  const canOfferGenerate = !!currentUser?.isScoAdmin && !parentCommentId && !!threadId;
  const draftsQuery = useQuery(ChatMessageComposer.queries.myDrafts(), {
    skip: !threadId || !canUseDrafts,
    variables: {
      collectionId: `ChatThread:${threadId}:${parentCommentId || 0}:drafts`,
      order: [['updatedAt', 'DESC']],
      pageSize: 50,
      parentCommentId: parentCommentId || null,
      threadId
    }
  });
  const drafts = draftsQuery.data?.forum?.comments?.items || [];
  const [upsertAttachmentUserPresence] = useMutation(gql`
    mutation ($input: UpsertAttachmentUserPresenceInput!) {
      upsertAttachmentUserPresence(input: $input) {
        success
      }
    }
  `);
  const attachmentType = props.newCommentValues.parentCommentId ? 'Comment' : 'Thread';
  const attachmentId = props.newCommentValues.parentCommentId || props.newCommentValues.threadId;

  // Submit Handler
  const commentInput = (isDraft: boolean) => {
    const allowFiles = props.enableFileAttachments !== false;
    const fileIds = allowFiles ? storedFileIds : [];
    return {
      body: body || '',
      ...props.newCommentValues,
      ...(draftCommentId ? {
        commentId: draftCommentId
      } : {}),
      isDraft,
      ...(fileIds.length > 0 ? {
        storedFileIds: fileIds
      } : {})
    };
  };
  const canCompose = () => {
    const allowFiles_0 = props.enableFileAttachments !== false;
    const fileIds_0 = allowFiles_0 ? storedFileIds : [];
    return stripMarkdownWhitespace(body).length > 0 || fileIds_0.length > 0;
  };
  const handleSubmit = async () => {
    if (!canCompose() || isUploading) {
      return;
    }
    let mutationResponse = null;
    setIsLoading(true);
    try {
      mutationResponse = await upsertChatComment({
        variables: {
          input: commentInput(false)
        }
      });
      setBody('');
      setDraftCommentId(null);
      setInitialMarkdown('');
      setEditorSeed(`sent-${Date.now()}`);
      reset();
      await draftsQuery.refetch();
      setSubmissionCount(count => count + 1);
      if (props.onSuccess) {
        await props.onSuccess(mutationResponse);
      }
    } catch (err) {
      if (props.onError) {
        await props.onError(err);
      } else {
        alert('Comment could not be posted.');
      }
    }
    setIsLoading(false);
  };
  const clearComposer = (seed: string) => {
    setDraftCommentId(null);
    setBody('');
    setInitialMarkdown('');
    setEditorSeed(seed);
    reset();
  };
  const saveDraft = async () => {
    if (!canCompose() || isUploading) {
      return;
    }
    const updatingOpenDraft = !!draftCommentId;
    setIsLoading(true);
    try {
      await upsertChatComment({
        variables: {
          input: commentInput(true)
        }
      });
      await draftsQuery.refetch();
      // A fresh save is a new draft. Clear the box so the next save is another one.
      // Saving while a draft is open updates that draft and leaves it open.
      if (!updatingOpenDraft) {
        clearComposer(`saved-${Date.now()}`);
      }
    } catch (err_0) {
      if (props.onError) {
        await props.onError(err_0);
      } else {
        alert('Draft could not be saved.');
      }
    }
    setIsLoading(false);
  };
  const sendDraft = async (draft: ChatDraft) => {
    if (!draft.commentId || isUploading) {
      return;
    }
    setIsLoading(true);
    try {
      const mutationResponse_0 = await upsertChatComment({
        variables: {
          input: {
            body: draft.body || '',
            ...props.newCommentValues,
            commentId: draft.commentId,
            isDraft: false
          }
        }
      });
      if (draftCommentId === draft.commentId) {
        clearComposer(`sent-${Date.now()}`);
        setSubmissionCount(count_0 => count_0 + 1);
      }
      await draftsQuery.refetch();
      if (props.onSuccess) {
        await props.onSuccess(mutationResponse_0);
      }
    } catch (err_1) {
      if (props.onError) {
        await props.onError(err_1);
      } else {
        alert('Draft could not be posted.');
      }
    }
    setIsLoading(false);
  };
  const loadDraft = (draft_0: ChatDraft) => {
    if (!draft_0.commentId) {
      return;
    }
    const nextBody = draft_0.body || '';
    setDraftCommentId(draft_0.commentId);
    setBody(nextBody);
    setInitialMarkdown(nextBody);
    setEditorSeed(draft_0.commentId);
    reset();
  };
  const removeDraft = async (commentId: string) => {
    setIsLoading(true);
    try {
      await deleteComment({
        variables: {
          input: {
            commentId
          }
        }
      });
      if (draftCommentId === commentId) {
        setDraftCommentId(null);
        setBody('');
        setInitialMarkdown('');
        setEditorSeed(`cleared-${Date.now()}`);
      }
      await draftsQuery.refetch();
    } catch (err_2) {
      if (props.onError) {
        await props.onError(err_2);
      } else {
        alert('Draft could not be deleted.');
      }
    }
    setIsLoading(false);
  };

  // Handle Submit Trigger
  useEffect(() => {
    if (submitTrigger > 0) {
      handleSubmit();
    }
  }, [submitTrigger]);

  // Typing Handler
  useEffect(() => {
    setIsTyping(stripMarkdownWhitespace(body).length > 0 && body != debouncedBody);
  }, [body, debouncedBody]);
  useEffect(() => {
    if (attachmentType && attachmentId) {
      upsertAttachmentUserPresence({
        variables: {
          input: {
            attachmentType,
            attachmentId,
            isTyping,
            isOnline: true,
            userId: currentUser?.userId
          }
        }
      });
    }
  }, [isTyping]);
  if (!currentUser) {
    return <Spinner />;
  }
  return <Box
  // Size container so ChatConnectionStatus density="auto" can hide the label when tight.
  // Tighter top so messages sit closer to the field now that connection status is in-toolbar.
  css={{
    containerType: 'inline-size'
  }} paddingBottom={2} paddingTop={1} paddingX={2} width={'full'} {...props.boxProps}>
      {/* Only takes space while someone is typing — no reserved gap above the field */}
      <ChatTypingIndicator attachmentId={attachmentId} attachmentType={attachmentType} />
      <OnboardingHighlightTarget borderRadius={'lg'} id={ONBOARDING_HIGHLIGHT_IDS.aiChat}>
        <Box width={'full'}>
          <RichMarkdownInput key={`thread-${props.newCommentValues.threadId}-${editorSeed}-${submissionCount}`} autoFocus={submissionCount > 0} disabled={generateBusy} enableFileAttachments={props.enableFileAttachments !== false} fileAttachmentsDisabled={isFull || isUploading || isLoading || generateBusy} initialMarkdownValue={initialMarkdown} placeholder={props.placeholder || 'Type your reply...'} additionalToolbarButtons={<HStack align={'center'} flexShrink={0} gap={2}>
                <ChatConnectionStatus density={'auto'} />
                {canOfferGenerate && <ChatGenerateControl body={body} busy={isUploading || isLoading} threadId={threadId} userId={currentUser.userId} onPending={setGenerateBusy} onWrite={(markdown, writtenForThreadId) => {
            if (writtenForThreadId !== threadIdRef.current) {
              return;
            }
            setBody(markdown);
            setInitialMarkdown(markdown);
            setEditorSeed(`generated-${Date.now()}`);
          }} />}
                {canUseDrafts && <DraftSaveButton busy={isUploading || isLoading || generateBusy} canSave={canCompose()} drafts={drafts} openDraftId={draftCommentId} onDelete={removeDraft} onNew={() => clearComposer(`new-${Date.now()}`)} onOpen={loadDraft} onSave={() => saveDraft()} onSend={sendDraft} />}
                <IconButton aria-label={'Post'} borderRadius={'md'} disabled={isUploading || generateBusy} loading={isLoading || isUploading || generateBusy || upsertChatCommentMutation.loading} size={'sm'} variant={'cta'} onClick={() => handleSubmit()}>
                  <LuArrowUp size={18} />
                </IconButton>
              </HStack>} pluginConfigs={{
          hotKeys: {
            hotKeys: {
              enter: {
                func: () => setSubmitTrigger(x => x + 1)
              }
            }
          }
        }} onFileAttachmentSelected={addFile} onChange={values => {
          setBody(values.markdown);
        }} />
          <CommentPendingAttachments disabled={isLoading} files={pendingFiles} onRemove={removeFile} />
        </Box>
      </OnboardingHighlightTarget>
    </Box>;
};
ChatMessageComposer.mutations = {
  upsertChatComment: () => gql`
    mutation ($input: UpsertChatCommentInput!) {
      upsertChatComment(input: $input) {
        comment {
          id
          commentId
          isDraft
          ${COMMENT_STORED_FILES_SELECTION}
        }
      }
    }
  `,
  deleteComment: () => gql`
    mutation ($input: DeleteCommentInput!) {
      deleteComment(input: $input) {
        comment {
          id
          commentId
          deletedAt
        }
      }
    }
  `
};
ChatMessageComposer.queries = {
  myDrafts: () => gql`
    query (
      $collectionId: String
      $order: [[String]]
      $pageSize: Int
      $parentCommentId: String
      $threadId: String
    ) {
      forum {
        id
        comments(
          collectionId: $collectionId
          onlyMyDrafts: true
          order: $order
          pageSize: $pageSize
          parentCommentId: $parentCommentId
          threadId: $threadId
        ) {
          id
          items {
            id
            commentId
            body
            isDraft
            createdAt
            updatedAt
          }
        }
      }
    }
  `
};
export const stripMarkdownWhitespace = (str: string) => {
  return (str || '').replaceAll('\n', '').replaceAll('\r', '').replaceAll('<br>', '').trim();
};
function DraftSaveButton(props) {
  const $ = _c(33);
  const count = props.drafts.length;
  const saveDisabled = props.busy || count === 0 && !props.canSave;
  const t0 = count > 0 ? `Save draft, ${count} saved` : "Save draft";
  let t1;
  if ($[0] !== saveDisabled) {
    t1 = saveDisabled ? {
      pointerEvents: "none"
    } : undefined;
    $[0] = saveDisabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = () => {
      if (props.canSave) {
        props.onSave();
      }
    };
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LuSave size={18} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== saveDisabled || $[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t4 = <IconButton aria-label={t0} borderRadius="md" css={t1} disabled={saveDisabled} size="sm" variant="ghost" onClick={t2}>{t3}</IconButton>;
    $[5] = saveDisabled;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== count) {
    t5 = count > 0 && <Box alignItems="center" bg="fg" borderRadius="full" boxShadow="0 0 0 2px var(--chakra-colors-bg)" color="bg" display="flex" fontSize="10px" fontWeight="semibold" height="16px" justifyContent="center" lineHeight="1" minWidth="16px" paddingX="3px" pointerEvents="none" position="absolute" right="-3px" top="-3px">{count}</Box>;
    $[10] = count;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <Box display="inline-flex" position="relative">{t4}{t5}</Box>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const button = t6;
  if (count === 0) {
    let t7;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        placement: "top"
      };
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== button) {
      t8 = <Tooltip borderRadius="6px" content="Save draft" fontSize="11px" fontWeight="bold" letterSpacing="0.05em" positioning={t7} textTransform="uppercase">{button}</Tooltip>;
      $[16] = button;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      placement: "top-end"
    };
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== button) {
    t8 = <HoverCardTrigger asChild={true}>{button}</HoverCardTrigger>;
    $[19] = button;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Badge color="colorPalette.fg/50" paddingX={2} paddingY={1} size="xs" variant="title">Drafts</Badge>;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== props.onNew || $[23] !== props.openDraftId) {
    t10 = props.openDraftId && <HStack _hover={{
      bg: "colorPalette.subtle"
    }} borderRadius="md" cursor="pointer" gap={2} paddingX={2} paddingY={1} onClick={props.onNew}><LuPlus size={14} /><Text fontSize="sm">New draft</Text></HStack>;
    $[22] = props.onNew;
    $[23] = props.openDraftId;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== props) {
    t11 = props.drafts.map(draft => {
      if (!draft.commentId) {
        return null;
      }
      const preview = stripMarkdownWhitespace(draft.body || "") || "Empty draft";
      const open = draft.commentId === props.openDraftId;
      const canSend = stripMarkdownWhitespace(draft.body || "").length > 0;
      return <HStack key={draft.commentId} borderRadius="md" gap={0} paddingY={0.5}><Box _hover={{
          bg: "colorPalette.subtle"
        }} aria-current={open ? "true" : undefined} aria-label={`Edit draft ${preview}`} as="button" bg={open ? "colorPalette.subtle" : "transparent"} borderRadius="md" flex={1} minWidth={0} paddingX={2} paddingY={1} textAlign="left" type="button" onClick={() => props.onOpen(draft)}><Text fontSize="sm" fontWeight={open ? "medium" : "normal"} lineClamp={1}>{preview}</Text><Text color="colorPalette.fg/50" fontSize="xs">{draftAge(draft.updatedAt || draft.createdAt)}</Text></Box><IconButton aria-label={`Send draft ${preview}`} disabled={!canSend || props.busy} size="xs" variant="ghost" onClick={() => props.onSend(draft)}><LuSend size={14} /></IconButton><IconButton aria-label={`Delete draft ${preview}`} disabled={props.busy} size="xs" variant="ghost" onClick={() => props.onDelete(draft.commentId as string)}><LuTrash2 size={14} /></IconButton></HStack>;
    });
    $[25] = props;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11) {
    t12 = <HoverCardContent padding={1} width="16rem">{t9}<Box maxHeight="14rem" overflowY="auto">{t10}{t11}</Box></HoverCardContent>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t12 || $[31] !== t8) {
    t13 = <HoverCardRoot closeDelay={200} openDelay={150} positioning={t7}>{t8}{t12}</HoverCardRoot>;
    $[30] = t12;
    $[31] = t8;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
}
function draftAge(value?: string | null) {
  if (!value) {
    return '';
  }
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) {
    return 'Just now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}